import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useWatch } from 'react-hook-form';
import { useOrgGroups } from '../../../../hooks/useOrgGroups';
import { AccessDurationMode } from '../../constants';
import type { UserFormValues } from '../../../../types/UserManagementTypes';
import { EMPTY_VALUE, FIELD_LABELS, STEP_HEADINGS } from './constants';
import styles from './UserFormModal.module.scss';

const SUMMARY_DATE_FORMAT = 'MMMM D, YYYY';
const END_OF_SUBSCRIPTION_LABEL = 'End of Subscription';

const SummaryItem = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.summaryItem}>
    <dt className={styles.summaryLabel}>{label}</dt>
    <dd className={styles.summaryValue}>{value || EMPTY_VALUE}</dd>
  </div>
);

const SummaryStep = () => {
  const values = useWatch<UserFormValues>() as UserFormValues;
  const { groups } = useOrgGroups();

  const groupNames = useMemo(() => {
    const selected = new Set(values.groupIds);
    return [...groups.filter((group) => selected.has(String(group.id))).map((group) => group.name), ...values.newGroupNames].join(', ');
  }, [groups, values.groupIds, values.newGroupNames]);

  const accessDuration =
    values.accessDurationMode === AccessDurationMode.END_OF_SUBSCRIPTION
      ? END_OF_SUBSCRIPTION_LABEL
      : values.accessExpiryDate
        ? dayjs(values.accessExpiryDate).format(SUMMARY_DATE_FORMAT)
        : '';

  return (
    <section className={styles.step}>
      <h3 className={styles.stepHeading}>{STEP_HEADINGS.SUMMARY}</h3>
      <dl className={styles.summaryGrid}>
        <SummaryItem label={FIELD_LABELS.NAME} value={values.name} />
        <SummaryItem label={FIELD_LABELS.TITLE} value={values.title} />
        <SummaryItem label={FIELD_LABELS.EMAIL} value={values.email} />
        <SummaryItem label={FIELD_LABELS.ACCESS} value={values.role ?? ''} />
        <SummaryItem label={FIELD_LABELS.PHONE} value={values.phone} />
        <SummaryItem label="GROUPS" value={groupNames} />
        <SummaryItem label={STEP_HEADINGS.ACCESS_DURATION} value={accessDuration} />
      </dl>
    </section>
  );
};

export default SummaryStep;
